import { RouteLocationNormalized, Router } from 'vue-router'
import { msalInstance, loginRequest } from '../authConfig'
import { InteractionType, PopupRequest, PublicClientApplication, RedirectRequest } from '@azure/msal-browser'
import { ToastService } from '../services/toast-service'
import { useAppConfigStore } from '../stores/app-config-store'
import piniaStores from '@/stores'
import { useAuthStore } from '@/stores/auth-store'
import { acquireAndProcessIdTokenClaims, getPWRoles } from '@/azure/token-utils'
import { useAppLoadingStore } from '@/stores/app-loading-store'

export function registerGuard(router: Router) {
  const appLoadingStore = useAppLoadingStore(piniaStores)

  router.onError((error) => {
    console.error('[router] navigation error:', error)
    appLoadingStore.reset()
    ToastService.storeToastMessageJson({
      message: 'Something went wrong while navigating. Please try again.',
      color: 'danger',
      position: 'bottom-right',
      closeable: true,
      category: 'general',
    })
  })

  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const shouldTrackLoading = !!to.meta.requiresAuth
    if (shouldTrackLoading) {
      appLoadingStore.start()
    }

    try {
      if (!to.meta.requiresAuth) {
        return true
      }

      const getClientSegmentFromRoute = () => {
        // For client routes we use the first URL segment as the client segment.
        const anyRouteRecordIsCheckClients = to.matched.some((record) => record.name === 'check-clients')
        if (anyRouteRecordIsCheckClients) {
          const segment = (to.params.clientCode as string) || (to.path.split('/')[1] as string)
          return segment || undefined
        }
        return undefined
      }

      const request = {
        ...loginRequest,
        redirectStartPage: to.fullPath,
      }
      const shouldProceed = await isAuthenticated(msalInstance)

      if (!shouldProceed) {
        const clientSegment = getClientSegmentFromRoute()

        const appConfigStore = useAppConfigStore(piniaStores)
        const clientCode = clientSegment
          ? appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP?.[clientSegment]?.clientCode
          : undefined

        // Persist auth context so we can restore it after authentication completes.
        try {
          sessionStorage.setItem('redirectStartPage', to.fullPath)
          if (clientSegment) {
            sessionStorage.setItem('clientSegment', clientSegment)
          } else {
            sessionStorage.removeItem('clientSegment')
          }
          if (clientCode) {
            sessionStorage.setItem('clientCode', clientCode)
          } else {
            sessionStorage.removeItem('clientCode')
          }
        } catch (e) {
          console.warn('[Guard] Failed to persist auth context in sessionStorage:', e)
        }

        // Redirect directly to B2C login (skip the intermediate Login.vue page)
        // The B2C custom login page is already branded based on clientSegment.
        console.info('[Guard] Unauthenticated user, redirecting directly to B2C login...', {
          clientSegment,
          redirectStartPage: to.fullPath,
        })

        try {
          await msalInstance.loginRedirect({
            ...request,
            redirectStartPage: to.fullPath,
            extraQueryParameters: {
              ...(clientSegment ? { clientSegment } : {}),
              originUrl: typeof window !== 'undefined' ? window.location.origin : '',
            },
          })
          // loginRedirect navigates away, so we return false to cancel the current navigation.
          return false
        } catch (loginError) {
          console.error('[Guard] loginRedirect failed, falling back to Login.vue:', loginError)

          // Store the error for Login.vue to display
          try {
            sessionStorage.setItem(
              'authError',
              JSON.stringify({
                message: loginError instanceof Error ? loginError.message : 'Login redirect failed',
                timestamp: Date.now(),
              }),
            )
          } catch {
            // ignore
          }

          // Fallback to Login.vue to display error
          ToastService.storeToastMessageJson({
            message: 'Unable to redirect to login. Please try again.',
            color: 'danger',
            position: 'bottom-right',
            closeable: true,
            category: 'auth',
          })

          return {
            name: 'login',
            query: {
              ...(clientSegment ? { clientSegment } : {}),
              ...(clientCode ? { clientCode } : {}),
              redirectStartPage: to.fullPath,
            },
          }
        }
      } else {
        // log to.path
        console.log('Guard to.path: ', to.path)

        let _idTokenClaims: Record<string, any> | null = null
        try {
          _idTokenClaims = await acquireAndProcessIdTokenClaims(msalInstance)
        } catch (error: any) {
          const errorCode = error?.errorCode || error?.code || ''
          console.error('[Guard] Failed to acquire/process idTokenClaims:', { errorCode, error })

          ToastService.storeToastMessageJson({
            message: 'Your session needs to be refreshed. Redirecting to sign-in…',
            color: 'warning',
            position: 'bottom-right',
            closeable: true,
            category: 'auth',
          })

          // Best-effort interactive re-auth to recover from iframe timeouts
          try {
            // If token acquisition fails, send user to branded login page to start a fresh auth flow.
            return {
              name: 'login',
              query: {
                ...(getClientSegmentFromRoute() ? { clientSegment: getClientSegmentFromRoute() } : {}),
                redirectStartPage: to.fullPath,
              },
            }
          } catch (redirectError) {
            console.error('[Guard] loginRedirect failed:', redirectError)
            return '/login'
          }
        }

        // log idTokenClaims
        console.log('Guard idTokenClaims: ', _idTokenClaims)

        // futher check if the idTokenClaims's roles contains the required role(s)
        const requiredRoles: string[] = to.meta.requiredRoles as string[]
        const appConfigStore = useAppConfigStore()
        const segmentToClientDataMap = appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP
        // Only run this logic for check-clients route and its children
        const anyRouteRecordIsCheckClients = to.matched.some((record) => record.name === 'check-clients')
        if (anyRouteRecordIsCheckClients) {
          const segment = to.path.split('/')[1] as string
          const clientData = segmentToClientDataMap[segment]
          const clientCode = clientData?.clientCode
          const pwRoles = getPWRoles(_idTokenClaims)
          const isPWAdmin = pwRoles.includes('pw:ADMIN')
          const isLabAdmin = pwRoles.includes(`${clientCode}:ADMIN`)
          const isClientOnline = clientData?.status === 'active'
          // If user is PW Admin, allow all
          if (isPWAdmin) {
            return true
          }

          // Check subscription feature gating (for non-PW Admin users)
          const leafRoute = to.matched[to.matched.length - 1]
          const requiredFeature = leafRoute?.meta.requiredFeature as string | undefined
          if (requiredFeature) {
            // Check if the analytics module is enabled via environment flag
            if (
              requiredFeature === 'basicAnalytics' &&
              import.meta.env.VITE_FEATURE_ANALYTICS_MODULE_ENABLED === 'false'
            ) {
              ToastService.storeToastMessageJson({
                message: 'This feature is not available yet. Please check back later.',
                color: 'info',
                position: 'bottom-right',
                closeable: true,
                category: 'permission',
              })
              return { name: 'client-home', params: { clientCode: segment } }
            }

            const subscriptionFeatures = clientData?.subscriptionFeatures as Record<string, any> | undefined
            if (!subscriptionFeatures || !subscriptionFeatures[requiredFeature]) {
              ToastService.storeToastMessageJson({
                message: 'Your current subscription plan does not include this feature. Please upgrade your plan.',
                color: 'warning',
                position: 'bottom-right',
                closeable: true,
                category: 'permission',
              })
              return { name: 'client-pricing-plans', params: { clientCode: segment } }
            }
          }

          // If user is Lab Admin
          if (isLabAdmin) {
            if (!isClientOnline) {
              // Only allow access to the leaf route with labAdminAllowWhenOffline: true
              const leafRoute = to.matched[to.matched.length - 1]
              const allowWhenOffline = leafRoute?.meta.labAdminAllowWhenOffline
              if (!allowWhenOffline) {
                ToastService.storeToastMessageJson({
                  message: 'Your lab is offline. Please process payment to restore access.',
                  color: 'danger',
                  position: 'bottom-right',
                  closeable: true,
                  category: 'permission',
                })

                // log entry
                console.log('Guard redirecting to client-billing for clientCode: ', clientCode)

                return { name: 'client-billing', params: { clientCode } }
              }
            }
          }
          if (!isPWAdmin && !isLabAdmin) {
            // outright deny access if the client is offline, and the user is a doctor
            if (!isClientOnline) {
              ToastService.storeToastMessageJson({
                message: 'The lab portal is currently offline. Please contact the lab administrator.',
                color: 'danger',
                position: 'bottom-right',
                closeable: true,
                category: 'permission',
              })
              return '/login'
            }
          }
          // check if user has any role for this client
          const clientCodesFromPwRoles = pwRoles
            .map((role: string) => role.split(':')[0])
            .filter((value, index, self) => self.indexOf(value) === index)
          if (!clientCodesFromPwRoles.includes(clientCode) && !clientCodesFromPwRoles.includes('pw')) {
            ToastService.storeToastMessageJson({
              message: 'You do not have permission to access this page.',
              color: 'danger',
              position: 'bottom-right',
              closeable: true,
              category: 'permission',
            })
            return '/login'
          }
        }

        // log requiredRoles
        console.log('Guard requiredRoles: ', requiredRoles)

        if (requiredRoles && requiredRoles.length > 0) {
          if (_idTokenClaims) {
            const pwRoles = getPWRoles(_idTokenClaims)

            // log pwRoles
            console.log('Guard pwRoles: ', pwRoles)

            if (!pwRoles || pwRoles.length === 0) {
              // set toast message in localStorage
              ToastService.storeToastMessageJson({
                message: 'You have not been assigned with any role(s) yet to access this page.',
                color: 'danger',
                position: 'bottom-right',
                closeable: true,
                category: 'auth',
              })
              return '/login'
            } else {
              // useAppConfigStore
              const appConfigStore = useAppConfigStore()

              // load app config
              const segmentToClientDataMap = appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP

              const anyRouteRecordIsAdmin = to.matched.some((record) => record.name === 'admin')

              // log anyRouteRecordIsAdmin
              console.log('Guard anyRouteRecordIsAdmin: ', anyRouteRecordIsAdmin)

              if (anyRouteRecordIsAdmin) {
                // filter the roles from pwRoles by those with the correct clientCode that is "pw"
                const userRoles = pwRoles
                  .map((role: string) => role.split(':'))
                  .filter((clientCodeAndRole: string[]) => {
                    const clientCode = clientCodeAndRole[0]
                    return clientCode === 'pw'
                  })
                  .map((clientCodeAndRole: string[]) => clientCodeAndRole[1])

                // log userRoles
                console.log('Guard userRoles: ', userRoles)

                const hasRequiredRoles = requiredRoles.every((role: string) => userRoles.includes(role))
                if (!hasRequiredRoles) {
                  // set toast message in localStorage
                  ToastService.storeToastMessageJson({
                    message: 'You do not have permission to access this page.',
                    color: 'danger',
                    position: 'bottom-right',
                    closeable: true,
                    category: 'permission',
                  })
                  return '/login'
                }

                return true
              }

              const anyRouteRecordIsCheckClients = to.matched.some((record) => record.name === 'check-clients')

              // log anyRouteRecordIsCheckClients
              console.log('Guard anyRouteRecordIsCheckClients: ', anyRouteRecordIsCheckClients)

              if (anyRouteRecordIsCheckClients) {
                const segment = to.path.split('/')[1] as string

                // check if the route uri segment is in the clientUriSegments
                // const segment = to.path.split('/')[1] as string

                // filter the roles from pwRoles by those with the correct clientCode
                const userRoles = pwRoles.some((role: string) => role === 'pw:ADMIN') ? ['ADMIN'] : []
                const userRolesToAdd = pwRoles
                  .map((role: string) => role.split(':'))
                  .filter((clientCodeAndRole: string[]) => {
                    const clientCode = clientCodeAndRole[0]
                    return segmentToClientDataMap[segment]?.clientCode === clientCode
                  })
                  .map((clientCodeAndRole: string[]) => clientCodeAndRole[1])
                for (const role of userRolesToAdd) {
                  if (!userRoles.includes(role)) {
                    userRoles.push(role)
                  }
                }

                // log userRoles
                console.log('Guard userRoles: ', userRoles)

                const hasRequiredRoles = requiredRoles.every((role: string) => userRoles.includes(role))
                if (!hasRequiredRoles) {
                  // set toast message in localStorage
                  ToastService.storeToastMessageJson({
                    message: 'You do not have permission to access this page.',
                    color: 'danger',
                    position: 'bottom-right',
                    closeable: true,
                    category: 'permission',
                  })
                  return '/login'
                }

                return true
              }

              // fallback
              return true
            }
          }
        }
      }

      return shouldProceed || '/login'
    } finally {
      if (shouldTrackLoading) {
        appLoadingStore.stop()
      }
    }
  })
}

export async function isAuthenticated(instance: PublicClientApplication): Promise<boolean> {
  // If your application uses redirects for interaction, handleRedirectPromise must be called and awaited on each page load before determining if a user is signed in or not
  return instance
    .handleRedirectPromise()
    .then(() => {
      const accounts = instance.getAllAccounts()
      if (accounts.length > 0) {
        return true
      }

      return false
    })
    .catch(() => {
      return false
    })
}
